import PropTypes from 'prop-types'
import React from 'react'
import cx from 'classnames'
import sortBy from 'lodash/sortBy'

import {
    RATING_CARD_POSITION_PERSONAL,
    RATING_CARD_POSITION_CLAN_PERIODIC,
    RATING_CARD_POSITION_CLAN,
    RATING_CARD_POSITION_CLAN_PFP,
    RATING_CARD_POSITION_CLAN_BATTLES,
    LEADERBOARDS_PERSONAL_PERIODIC,
    LEADERBOARDS_CLAN_PERIODIC,
    LEADERBOARDS_CLAN,
    LEADERBOARDS_ALL_THE_TIME,
    LEADERBOARDS_RATING_DESCRIPTION_PERSONAL,
    LEADERBOARDS_DESCRIPTION_CLAN_PERIODIC,
    LEADERBOARDS_RATING_DESCRIPTION_CLAN,
    LEADERBOARDS_RATING_POSITION_PERSONAL,
    LEADERBOARDS_RATING_POSITION_CLAN,
    LEADERBOARDS_DESCRIPTION_TOP_PERSONAL,
    LEADERBOARDS_DESCRIPTION_TOP_CLAN,
    LEADERBOARDS_DESCRIPTION_CLAN_PERIODIC_NOT_DEFINED,
    LEADERBOARDS_RATING_DESCRIPTION_NOT_DEFINED,
} from './translations'

import { FormattedNumber } from 'react-intl'
import { CLIENT_REWARD_TYPE, LEADERBOARDS_TYPE, MAX_VISIBLE_REWARD_COUNT } from '../../constants'
import { getDatePeriod, getDateTimePeriod, getDateTime } from 'helpers/datetime'
import { Reward } from '../Reward/Reward'
import { RewardBox } from 'components/RewardBox/RewardBox'
import styles from './RatingCard.scss'

const getPositionClanText = (qualificationFp, qualificationBattles) => {
    switch (true) {
    case Boolean(qualificationFp && qualificationBattles):
        return RATING_CARD_POSITION_CLAN(qualificationFp, qualificationBattles)
    case Boolean(qualificationBattles):
        return RATING_CARD_POSITION_CLAN_BATTLES(qualificationBattles)
    case Boolean(qualificationFp):
        return RATING_CARD_POSITION_CLAN_PFP
    default:
        return null
    }
}

const getPeriod = (type, startTs, endTs, event) => {
    switch (type) {
    case LEADERBOARDS_TYPE.PERSONAL_PERIODIC:
        return getDateTimePeriod(startTs, endTs)
    case LEADERBOARDS_TYPE.CLAN_PERIODIC:
        return getDateTimePeriod(startTs, endTs)
    case LEADERBOARDS_TYPE.CLAN:
        return LEADERBOARDS_ALL_THE_TIME(getDatePeriod(event.startDate, event.endDate))
    }
}

const getDescriptionText = (type, hasRewards, prizePlaces, endTs) => {
    if (hasRewards) {
        switch (type) {
        case LEADERBOARDS_TYPE.PERSONAL_PERIODIC:
            return LEADERBOARDS_RATING_DESCRIPTION_PERSONAL
        case LEADERBOARDS_TYPE.CLAN_PERIODIC:
            return LEADERBOARDS_DESCRIPTION_CLAN_PERIODIC(getDateTime(endTs))
        case LEADERBOARDS_TYPE.CLAN:
            return LEADERBOARDS_RATING_DESCRIPTION_CLAN
        }
    }

    if (prizePlaces) {
        switch (type) {
        case LEADERBOARDS_TYPE.PERSONAL_PERIODIC:
            return LEADERBOARDS_DESCRIPTION_TOP_PERSONAL(<FormattedNumber value={prizePlaces}/>)
        case LEADERBOARDS_TYPE.CLAN:
            return LEADERBOARDS_DESCRIPTION_TOP_CLAN(<FormattedNumber value={prizePlaces}/>)
        case LEADERBOARDS_TYPE.CLAN_PERIODIC:
            return LEADERBOARDS_DESCRIPTION_CLAN_PERIODIC_NOT_DEFINED
        }
    }

    return LEADERBOARDS_RATING_DESCRIPTION_NOT_DEFINED
}

const getTitleText = (type) => {
    switch (type) {
    case LEADERBOARDS_TYPE.PERSONAL_PERIODIC:
        return LEADERBOARDS_PERSONAL_PERIODIC
    case LEADERBOARDS_TYPE.CLAN_PERIODIC:
        return LEADERBOARDS_CLAN_PERIODIC
    case LEADERBOARDS_TYPE.CLAN:
        return LEADERBOARDS_CLAN
    }
}

const getPositionText = (place, type, qualificationFp, qualificationBattles) => {
    if (place) {
        return (
            <div>
                <div className={styles.positionNumber}>
                    <FormattedNumber value={place}/>
                </div>
                <div className={styles.positionText}>
                    {type === LEADERBOARDS_TYPE.PERSONAL_PERIODIC ?
                        LEADERBOARDS_RATING_POSITION_PERSONAL :
                        LEADERBOARDS_RATING_POSITION_CLAN
                    }
                </div>
            </div>
        )
    }

    switch (type) {
    case LEADERBOARDS_TYPE.PERSONAL_PERIODIC:
        return (
            <div className={styles.positionDescription}>
                {RATING_CARD_POSITION_PERSONAL(qualificationFp)}
            </div>
        )
    case LEADERBOARDS_TYPE.CLAN_PERIODIC:
        return (
            <div className={styles.positionDescription}>
                {RATING_CARD_POSITION_CLAN_PERIODIC}
            </div>
        )
    case LEADERBOARDS_TYPE.CLAN:
        return (
            <div className={styles.positionDescription}>
                {getPositionClanText(qualificationFp, qualificationBattles)}
            </div>
        )
    }
}

const RatingCard = (props) => {
    const {
        place,
        type,
        rewards = [],
        prizePlaces,
        startTs,
        endTs,
        qualificationFp,
        qualificationBattles,
        event,
    } = props

    const hasRewards = rewards && rewards.length > 0
    const hasRewardsInBox = hasRewards && rewards.length > MAX_VISIBLE_REWARD_COUNT

    const sortedRewards = sortBy(rewards, ['priority'])
    const visibleRewards = hasRewardsInBox ? sortedRewards.slice(0, MAX_VISIBLE_REWARD_COUNT - 1) : sortedRewards
    const rewardsInBox = (
        hasRewardsInBox ? sortedRewards.slice(MAX_VISIBLE_REWARD_COUNT - sortedRewards.length - 1).map(item => ({
            id: item.id,
            type: CLIENT_REWARD_TYPE[item.type],
            value: item.amount,
        })) : []
    )

    return (
        <div className={styles.base}>
            <div className={styles.ratingHeader}>
                <div className={styles.title}>
                    {getTitleText(type)}
                </div>
                <div className={styles.date}>
                    {getPeriod(type, startTs, endTs, event)}
                </div>
            </div>
            <div className={cx(styles.positionBlock, !place && styles.positionBlock__noPosition )}>
                {getPositionText(place, type, qualificationFp, qualificationBattles)}
            </div>
            <div className={styles.footer}>
                <div className={cx(styles.rewards, hasRewards && styles.rewards__granted)}>
                    {visibleRewards.map((item, i) => <Reward key={i} reward={item}/>)}
                    {hasRewardsInBox && (
                        <RewardBox
                            className={styles.rewardBox}
                            rewards={rewardsInBox}
                        />
                    )}
                </div>
                <div className={styles.description}>
                    {getDescriptionText(type, hasRewards, prizePlaces, endTs)}
                </div>
            </div>
        </div>
    )
}

RatingCard.propTypes = {
    type: PropTypes.string.isRequired,
    place: PropTypes.number,
    rewards: PropTypes.array,
    prizePlaces:PropTypes.number,
    startTs: PropTypes.number,
    endTs: PropTypes.number,
    qualificationFp: PropTypes.number,
    qualificationBattles: PropTypes.number,
    event: PropTypes.object,
}

export default RatingCard



// WEBPACK FOOTER //
// ./containers/WGCWXLeaderboards/components/RatingCard/RatingCard.jsx